/**
 * Profile Service
 * Handles public profile API calls
 */

import axios from 'axios';
import type { ComponentAchievement, FormErrors, User } from '../types/index';
import { getAxiosErrorMessage } from '../utils/error';
import { deviceTimeZone } from '../utils/listening';
import api from './api';

const API_URL = import.meta.env.VITE_API_URL;

// ============================================
// Response Types
// ============================================

/** One coursework bucket (User::courseworkSnapshot). */
export interface PublicCourseworkBucket {
  score: number;
  rank: number | null;
  completed: number;
  total: number;
}

/** The Free / Premium / Overall block the public profile renders. */
export interface PublicCoursework {
  free: PublicCourseworkBucket;
  premium: PublicCourseworkBucket;
  overall: PublicCourseworkBucket;
}

/** `stats` of User::getPublicProfile. */
export interface PublicProfileStats {
  sessionsGiven: number;
  minutesGiven: number;
  peopleHelped: number;
  averageRating: number | null;
  courseScore: number;
  courseScoreRank: number | null;
  masteryScore: number;
  masteryScoreRank: number | null;
  quizzesCompleted: number;
  totalQuizzes: number;
  totalPoints: number;
  pointsRank: number | null;
  courseCompleted: boolean;
  coursework: PublicCoursework;
}

/** One badge (BadgeService::getUserBadges). */
export interface PublicProfileBadge {
  type: string;
  icon: string;
  label: string;
  description: string;
  tier: string;
}

/** One appreciation message (User::getAppreciationWall). */
export interface PublicAppreciation {
  text: string;
  rating: number;
  month: string;
}

/** A public, active circle the user belongs to (PublicProfileController). */
export interface PublicProfileCohort {
  id: number;
  name: string;
  color: string | null;
  members_count: number;
}

/**
 * GET /u/{username}: User::getPublicProfile (server/app/Models/User.php)
 * plus what PublicProfileController layers on. Pinned to the PHP by
 * services/__tests__/public-profile-shape.test.ts through its fixture.
 */
export interface PublicProfile {
  id: number;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  bio: string | null;
  /** The join date, date only: "Y-m-d". */
  memberSince: string;
  badges: PublicProfileBadge[];
  stats: PublicProfileStats;
  isLive: boolean;
  /** null when the cached payload was built without a viewer (always, today). */
  isFavorited: boolean | null;
  favoritesCount: number;
  appreciationWall: PublicAppreciation[];
  circleSessionsAttended: number;
  recurringGroups: number;
  publicCohorts: PublicProfileCohort[];
  /**
   * "Open to listen" (B7): ABSENT for a guest, the giver themself or a
   * barred pair. Read through readProfileListening, which validates it.
   */
  listening?: unknown;
}

interface GetPublicProfileSuccessResponse {
  success: true;
  profile: PublicProfile;
}

interface GetPublicProfileErrorResponse {
  success: false;
  error: string;
  status?: number;
}

type GetPublicProfileResponse = GetPublicProfileSuccessResponse | GetPublicProfileErrorResponse;

interface UpdateProfileSuccessResponse {
  success: true;
  user: User;
}

interface UpdateProfileErrorResponse {
  success: false;
  error: string;
  errors?: FormErrors;
}

type UpdateProfileResponse = UpdateProfileSuccessResponse | UpdateProfileErrorResponse;

interface ResendVerificationSuccessResponse {
  success: true;
  message: string;
}

interface ResendVerificationErrorResponse {
  success: false;
  error: string;
}

type ResendVerificationResponse =
  | ResendVerificationSuccessResponse
  | ResendVerificationErrorResponse;

interface VerifyEmailSuccessResponse {
  success: true;
  message: string;
  data?: unknown;
}

interface VerifyEmailErrorResponse {
  success: false;
  error: string;
}

type VerifyEmailResponse = VerifyEmailSuccessResponse | VerifyEmailErrorResponse;

interface ProfileUpdateData {
  name?: string;
  username?: string;
  email?: string;
  bio?: string;
  avatar_url?: string;
  [key: string]: unknown;
}

/**
 * Get a user's public profile by username. Sends the device's zone as `tz`
 * (D19) so "Open to listen" hours are on the viewer's own clock.
 */
export const getPublicProfile = async (username: string): Promise<GetPublicProfileResponse> => {
  try {
    const tz = deviceTimeZone();
    const response = await api.get(
      `/u/${encodeURIComponent(username)}`,
      tz ? { params: { tz } } : undefined,
    );
    return { success: true, profile: response.data.profile };
  } catch (error) {
    // Extract status code for 404 handling
    const status =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to fetch profile', {
        log: 'ProfileService.getPublicProfile',
      }),
      status,
    };
  }
};

/**
 * One earned achievement on someone's public profile. The server sends only
 * completed all-time achievements, with the display fields filled in.
 */
export type PublicAchievement = ComponentAchievement;

type GetUserAchievementsResponse =
  | { success: true; achievements: PublicAchievement[] }
  | { success: false; error: string; status?: number };

/**
 * Get the achievements a user has earned, for their public profile.
 *
 * GET /achievements/user/{username} answers
 * `{ achievements, total_earned, total_points }` at the TOP level
 * (AchievementController::userAchievements), with no `data` envelope.
 * Only the array is returned: the count is derived from it
 * (countEarnedAchievements) so every counter on the page reads one set.
 */
export const getUserAchievements = async (
  username: string,
): Promise<GetUserAchievementsResponse> => {
  try {
    const response = await api.get<{ achievements?: unknown }>(
      `/achievements/user/${encodeURIComponent(username)}`,
    );
    const list = response.data?.achievements;
    return {
      success: true,
      achievements: Array.isArray(list) ? (list as PublicAchievement[]) : [],
    };
  } catch (error) {
    const status =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to fetch achievements', {
        log: 'ProfileService.getUserAchievements',
      }),
      status,
    };
  }
};

/**
 * Update the current user's profile
 */
export const updateMyProfile = async (
  profileData: ProfileUpdateData,
): Promise<UpdateProfileResponse> => {
  try {
    const response = await api.put('/profile', profileData);
    return { success: true, user: response.data.user };
  } catch (error) {
    // Extract form validation errors if present
    const errors =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { errors?: FormErrors } } }).response?.data?.errors
        : undefined;
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to update profile', {
        log: 'ProfileService.updateMyProfile',
      }),
      errors,
    };
  }
};

/**
 * Resend email verification link
 */
export const resendVerificationEmail = async (): Promise<ResendVerificationResponse> => {
  try {
    const response = await api.post('/email/resend-verification');
    return { success: true, message: response.data.message };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to send verification email', {
        log: 'ProfileService.resendVerificationEmail',
      }),
    };
  }
};

/**
 * Change an unverified account's email (typo-lockout recovery) and re-send the
 * verification link. Server applies the same canonicalization + disposable checks
 * as registration.
 */
export const changeUnverifiedEmail = async (email: string): Promise<ResendVerificationResponse> => {
  try {
    const response = await api.post('/email/change', { email });
    return { success: true, message: response.data.message };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to update email', {
        log: 'ProfileService.changeUnverifiedEmail',
      }),
    };
  }
};

/**
 * Verify email with signed URL parameters
 * This is called when user clicks the verification link
 * Note: Uses plain axios (not authenticated api) because this is a public endpoint
 * accessed via email link - user may not be logged in
 */
export const verifyEmail = async (
  id: string | number,
  hash: string,
  signature: string,
  expires: string,
): Promise<VerifyEmailResponse> => {
  try {
    const params = new URLSearchParams({ signature, expires });
    const response = await axios.get(`${API_URL}/email/verify/${id}/${hash}?${params}`);
    return { success: true, message: response.data.message, data: response.data.data };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to verify email', {
        log: 'ProfileService.verifyEmail',
      }),
    };
  }
};

// ============================================
// Circle accessibility preferences
// ============================================

/**
 * The display preferences — docs/plans/CIRCLE-AMBIENT-VIEW.md.
 *
 * Columns on `users`, not a JSON blob: every other per-user toggle on this app
 * is a column (`push_enabled`, `marketing_emails_enabled`,
 * `data_collection_consent`), and being one means `User::toArray()` carries
 * them on the auth payload for free.
 */
export interface DisplayPreferences {
  /** 161% faces on the ambient page — the last safe step of the sizing study. */
  circle_larger_faces: boolean;
  /** Land on the grid in EVERY phase, not just `discussion`. */
  circle_start_on_grid: boolean;
  /**
   * Force the metered encoder profile everywhere — 480×480 at 400 kbps on a
   * 1:1 call instead of 640×640 at 900.
   *
   * NOT circle-prefixed, and the interface lost its `Circle` prefix with it:
   * the circle's encode is a SERVER budget (an SFU forwards each feed to ten
   * more people) and is not this switch's to move. What it is for is the 1:1,
   * where the link detection is Chromium-only and a tethered laptop looks
   * exactly like a fibre one.
   */
  video_data_saver: boolean;
}

/** @deprecated the name from before "Data saver", which is not circle-only. */
export type CircleDisplayPreferences = DisplayPreferences;

/**
 * RETURNS THE SERVER'S USER, NOT VOID (class B).
 *
 * A toggle whose save cannot refuse is a toggle that lies: the switch moves,
 * the next circle is unchanged, and nobody finds out until they are in one.
 * The caller branches on `success` and puts the switch back where it was.
 *
 * Rides PUT /profile — the endpoint that already carries the one per-user
 * boolean that is not a notification (`data_collection_consent`) — rather than
 * a new route, so there is exactly one place a user preference is written.
 */
export const updateDisplayPreferences = async (
  prefs: Partial<DisplayPreferences>,
): Promise<UpdateProfileResponse> => {
  try {
    const response = await api.put<{ data?: { user: User } }>('/profile', prefs);
    const user = response.data?.data?.user;
    if (!user) {
      // A 200 with no user is not a save we can confirm, and reporting it as
      // one would leave the switch showing a value the server never stored.
      return { success: false, error: 'Save failed — try again' };
    }
    return { success: true, user };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to save preference', {
        log: 'ProfileService.updateDisplayPreferences',
      }),
    };
  }
};

// ============================================
// 1:1 matching
// ============================================

/**
 * "Only match with people who completed the intro" — one per-user setting for
 * both 1:1 roles (COHORT-COMMUNICATION.md X3), written from the 1:1
 * preferences modal and from Profile.
 *
 * Rides PUT /profile like the display preferences above, for the same reason:
 * it is a column on `users`, so /me carries it and both entry points read and
 * write ONE copy. Returns the server's user, never void (class B): the caller
 * puts the switch back and says why when this refuses.
 */
export const updateIntroMatchFilter = async (enabled: boolean): Promise<UpdateProfileResponse> => {
  try {
    const response = await api.put<{ data?: { user: User } }>('/profile', {
      only_match_intro_completed: enabled,
    });
    const user = response.data?.data?.user;
    if (!user) {
      return { success: false, error: "Couldn't save that setting. Try again." };
    }
    return { success: true, user };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, "Couldn't save that setting. Try again.", {
        log: 'ProfileService.updateIntroMatchFilter',
      }),
    };
  }
};
